import React, { useState } from 'react';
import { usePrograms } from '../contexts/ProgramsContext';
import { useUser } from '../contexts/UserContext';

interface EligibilityCacheExporterProps {
  onExportComplete?: (success: boolean, count: number) => void;
  onExportError?: (error: string) => void;
}

export const EligibilityCacheExporter: React.FC<EligibilityCacheExporterProps> = ({
  onExportComplete,
  onExportError,
}) => {
  const { exportEligibilityCacheToFirebase, programs } = usePrograms();
  const { user } = useUser();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!user) {
      onExportError?.('User not authenticated');
      return;
    }

    setIsExporting(true);
    try {
      await exportEligibilityCacheToFirebase();
      const programCount = programs.length;
      onExportComplete?.(true, programCount);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onExportError?.(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="eligibility-cache-exporter">
      <button
        onClick={handleExport}
        disabled={isExporting || !user}
        className="export-cache-button"
        style={{
          padding: '10px 20px',
          backgroundColor: isExporting ? '#ccc' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isExporting ? 'not-allowed' : 'pointer',
        }}
      >
        {isExporting ? 'Exporting...' : 'Export Eligibility Cache to Firebase'}
      </button>
    </div>
  );
};

// Hook version for programmatic use
export const useEligibilityCacheExport = () => {
  const { exportEligibilityCacheToFirebase } = usePrograms();
  const { user } = useUser();
  const [isExporting, setIsExporting] = useState(false);

  const exportCache = async (): Promise<{ success: boolean; count: number; error?: string }> => {
    if (!user) {
      return { success: false, count: 0, error: 'User not authenticated' };
    }

    setIsExporting(true);
    try {
      await exportEligibilityCacheToFirebase();
      return { success: true, count: 0 }; // Count will be logged in the function
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, count: 0, error: errorMessage };
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportCache,
    isExporting,
  };
};
